import { useMemo } from 'react';
import Star from '../../../assets/lucide/star.svg';
import Banknote from '../../../assets/lucide/banknote.svg';
import GraduationCap from '../../../assets/lucide/graduation-cap.svg';
import Medal from '../../../assets/lucide/medal.svg';
import Handshake from '../../../assets/lucide/handshake.svg';
import MessageSquareQuote from '../../../assets/lucide/message-square-quote.svg';
import MessageSquare from '../../../assets/lucide/message-square.svg';

export const usePlaybooks = (goals) =>
  useMemo(() => {
    const playbooks = [
      {
        id: 1,
        title: 'Find warm intros to your companies’ target accounts',
        description: 'Enter your company’s sales targets and find key intros.',
        goal: 'boost_sales',
        link: 'https://help.getro.com/support/solutions/articles/65000189773--sales-book-meetings-with-target-accounts-',
        icon: Handshake,
      },
      {
        id: 2,
        title: 'Find great talent for roles at your companies',
        description: 'Find top candidates that match roles at your companies.',
        goal: 'hire_talent',
        link: 'https://help.getro.com/support/solutions/articles/65000189775--talent-find-great-talent-for-open-roles',
        icon: Star,
      },
      {
        id: 3,
        title: 'Find niche talent with specific skills',
        description: 'Surface standout talent for hard-to-fill or strategic roles.',
        goal: 'boost_sales',
        link: 'https://help.getro.com/support/solutions/articles/65000189777--talent-find-niche-talent-with-specific-skills',
        icon: Medal,
      },
      {
        id: 4,
        title: 'Backchannel checks for hiring',
        description: 'Vet candidates through shared connections.',
        goal: 'hire_talent',
        link: 'https://help.getro.com/support/solutions/articles/65000189783--diligence-talent-conduct-backchannel-checks-for-hiring',
        icon: MessageSquareQuote,
      },
      {
        id: 5,
        title: 'Support your companies with fundraising',
        description: 'Connect your companies to relevant investors.',
        goal: 'find_investors',
        link: 'https://help.getro.com/support/solutions/articles/65000189798--fundraising-support-portcos-with-fundraising',
        icon: Banknote,
      },
      {
        id: 6,
        title: 'Validate market/team during diligence',
        description: 'Talk to experts or ex-colleagues to validate potential deals.',
        goal: 'find_investors',
        link: 'https://help.getro.com/support/solutions/articles/65000189780--diligence-vc-strategy-validate-market-team-during-diligence',
        icon: MessageSquareQuote,
      },
      {
        id: 7,
        title: 'Track and share trusted industry experts',
        description: 'And connect them with your companies.',
        goal: 'find_mentors',
        link: 'https://help.getro.com/support/solutions/articles/65000189778--diligence-company-strategy-connect-with-industry-experts-',
        icon: GraduationCap,
      },
      {
        id: 8,
        title: 'Make intros for events & conferences',
        description: 'Get your companies in touch with other relevant attendees.',
        goal: 'grow_network',
        link: 'https://help.getro.com/support/solutions/articles/65000189781--vc-strategy-make-intros-for-events-conferences',
        icon: MessageSquare,
      },
      {
        id: 9,
        title: 'Discover new deals & founders',
        description: 'Find and vet early-stage founders or startups.',
        goal: 'source_new_deals',
        link: 'https://help.getro.com/support/solutions/articles/65000189779--vc-strategy-discover-new-deals-founders',
        icon: Star,
      },
      {
        id: 10,
        title: 'Grow your deal pipeline',
        description: 'Gather insights on founders you’re considering for investment.',
        goal: 'source_new_deals',
        link: 'https://help.getro.com/support/solutions/articles/65000189782--diligence-vc-strategy-grow-your-deal-pipeline',
        icon: MessageSquareQuote,
      },
    ];

    const allGoals = new Set(goals);

    const filteredPlaybooks = playbooks.reduce(
      (acc, playbook) => {
        if (allGoals.has(playbook.goal) && !acc.set.has(playbook.goal) && acc.result.length < 3) {
          acc.result.push(playbook);
          acc.set.add(playbook.goal);
          acc.ids.push(playbook.id);
        }
        return acc;
      },
      {
        result: [],
        set: new Set(),
        ids: [],
      },
    );

    const getRandomPlaybooks = (numPlaybooks, excludedIds) => {
      const ids = new Set(excludedIds);
      const playbooksToFilter = playbooks.filter((playbook) => !ids.has(playbook.id));

      const shuffled = [...playbooksToFilter];
      for (let i = shuffled.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled.slice(0, numPlaybooks);
    };

    return filteredPlaybooks.result.length < 3
      ? filteredPlaybooks.result.concat(getRandomPlaybooks(3 - filteredPlaybooks.result.length, filteredPlaybooks.ids))
      : filteredPlaybooks.result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
